<script>
export default {
	name: 'ImageBanner',
	inheritAttrs: false,
};
</script>

<script setup>
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { isUrlMatchCurrentHostname } from '~/logic/helpers/string.js';
import AppHyperlink from '~/components/AppHyperlink.vue';


const props = defineProps({
	desktopImgSrc: { type: String, default: '' },
	mobileImgSrc: { type: String, default: '' },
	desktopBannerHeight: { type: String, default: 'small' }, // <--- 'small' only
	mobileBannerHeight: { type: String, default: 'small' }, // <--- 'small' | 'large'
	desktopPlaceholderHorizontalAlignment: { type: String, default: 'center' }, // <--- 'center' | 'left' | 'right'
	mobilePlaceholderHorizontalAlignment: { type: String, default: 'left' }, // <--- 'center' | 'left' | 'right'
	title: { type: String, default: '' },
	description: { type: String, default: '' },
	url: { type: String, default: '' },
	gradientOverlay: { type: String, default: 'none' }, // <--- 'none' | 'full'
	gradientOverlayOpacity: { type: String, default: '70' }, // <--- '30' | '50' | '70'
});

const computedGradientValue = computed(() => {
	if (isMobileViewport.value) {
		return 'to bottom, rgba(31, 64, 116, 0) 0%, rgba(31, 64, 116, 90) 40%, rgba(31, 64, 116, 100) 100%';
	}
	return `to ${props.keyVisualAlignment === 'left' ? 'right' : 'left'}, rgba(31, 64, 116, 0) 0%, rgba(31, 64, 116, 90) 40%, rgba(31, 64, 116, 100) 100%`;
});

const bannerSizeDesktopMap = {
	small: '328',
};

const bannerSizeMobileMap = {
	small: '160',
	large: '180',
};

const desktopPlaceholderHorizontalAlignmentMap = {
	left: '>md:justify-start',
	center: '>md:justify-center',
	right: '>md:justify-end',
};

const mobilePlaceholderHorizontalAlignmentMap = {
	left: 'md:justify-start',
	center: 'md:justify-center',
	right: 'md:justify-end',
};

const computedImgSrcBg = computed(() => {
	if (isMobileViewport.value && props.mobileImgSrc) return props.mobileImgSrc;
	return props.desktopImgSrc;
});


const aaFlag = computed(() => {
	return props.url ? 'enabled' : 'disabled';
});

const wrapperAttrs = computed(() => {
	if (props.url) {
		// <AppHyperlink> tag
		return {
			is: AppHyperlink,
			href: props.url,
			target: '_blank',
		};
	} else {
		// <div> tag
		return {
			is: 'div',
		};
	}
});


</script>



<template>
<component
	:is="props.url ? AppHyperlink : 'div'"
	v-bind="wrapperAttrs"
	class="ImageBanner w-full"
	:class="{
		'ib-use-overlay ib-overlay-full': gradientOverlay === 'full',
		'ib-overlay-opacity-50': gradientOverlayOpacity === '50',
		'ib-overlay-opacity-30': gradientOverlayOpacity === '30',
	}"
>
	<div
		class="ImageBanner-inner bg-center bg-cover relative"
		:style="{
			'background-image': `url(${computedImgSrcBg})`,
			'--BannerSizeDesktop': `${bannerSizeDesktopMap[desktopBannerHeight]}px`,
			'--BannerSizeMobile': `${bannerSizeMobileMap[mobileBannerHeight]}px`,
		}"
	>
		<div
			class="w-full h-full flex justify-center items-center"
			:class="`${desktopPlaceholderHorizontalAlignmentMap[desktopPlaceholderHorizontalAlignment]} ${mobilePlaceholderHorizontalAlignmentMap[mobilePlaceholderHorizontalAlignment]}`"
		>
			<div class="ImageBanner-text-placeholder">
				<slot></slot>
			</div>
		</div>
	</div>
</component>
</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.ImageBanner {
	position: relative;
	text-decoration: none;

	:deep(p > span:first-of-type) {
		@apply line-clamp-4;
	}

	@media #{var.$query-max-md} {
		:deep(p) {
			@apply my-0;
		}

		:deep(.CTAButton) {
			font-size: 12px;
			line-height: 18px;

			@apply px-4 py-2.5;
		}
	}
}

.ImageBanner-inner {
	height: var(--BannerSizeDesktop);
	width: 100%;
	background-repeat: no-repeat;
	position: relative;
	display: flex;
	align-items: center;
	overflow: hidden;
	border: 1px solid var(--neutral-grey-base);
	@apply rounded-3xl bg-neutral-white-base;
	
	@media #{var.$query-max-md} {
		height: var(--BannerSizeMobile);
		flex-wrap: wrap;
		align-items: flex-start;
		align-content: flex-start;
	}
}

.ib-use-overlay {
	&.ib-overlay-full::before {
		content: '';
		background-image: linear-gradient(to bottom, color.change(#333, $alpha: 0.7), color.change(#333, $alpha: 0));
		position: absolute;
		top: 0;
		bottom: 0;
		left: 0;
		right: 0;
		z-index: 10;
		opacity: 0.7;
		@apply rounded-3xl;
	}

	&.ib-overlay-opacity-30 {
		&::before,
		&::after {
			opacity: 0.3;
		}
	}

	&.ib-overlay-opacity-50 {
		&::before,
		&::after {
			opacity: 0.5;
		}
	}
}

.ImageBanner-text-placeholder {
	position: relative;
	max-width: 702px;
	z-index: 20;
	@apply px-8;

	@media #{var.$query-max-md} {
		width: calc(100% - 48px);
		@apply px-6;
	}
}

</style>
